// src/bootstrap/http/auth.js

import axios from 'axios'
import Vue from 'vue'
import router from '@/router'
import { isTokenExpired } from '@/utils/jwt'

function getToken () {
  return localStorage.getItem('token')
}

// Cancel sources for in-flight axios requests so we can abort them on 401.
const pendingSources = new Set()

// Idempotency guard — prevents a burst of 401s from triggering multiple
// concurrent redirects. Reset once we land on /login.
let unauthorizedHandled = false
function resetUnauthorizedFlag () {
  unauthorizedHandled = false
}

function handleUnauthorized () {
  if (unauthorizedHandled) return
  unauthorizedHandled = true

  localStorage.removeItem('token')

  // Abort everything still in flight so each one doesn't fire its own 401
  // handler / popup / redirect.
  pendingSources.forEach(source => {
    try { source.cancel('Unauthorized') } catch (e) { /* noop */ }
  })
  pendingSources.clear()

  if (router.currentRoute.name !== 'Login') {
    router.push({
      path: '/login',
      query: { redirect: router.currentRoute.fullPath }
    })
  }
}

// Reset the idempotency flag once navigation to /login completes, so a future
// session can hit the same path again.
router.afterEach(to => {
  if (to.name === 'Login') resetUnauthorizedFlag()
})

/* =========================
   AXIOS
   ========================= */

axios.interceptors.request.use(config => {
  const token = getToken()

  // Proactive expiry check: if we know the token is dead, short-circuit
  // before the request goes out. This is what prevents the 401-storm symptom
  // when the token expires while the user has the tab open.
  if (token && isTokenExpired(token)) {
    handleUnauthorized()
    const source = axios.CancelToken.source()
    config.cancelToken = source.token
    source.cancel('Token expired')
    return config
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // Track this request so we can cancel it if a 401 fires elsewhere.
  const source = axios.CancelToken.source()
  config.cancelToken = source.token
  config._cancelSource = source
  pendingSources.add(source)

  return config
})

axios.interceptors.response.use(
  response => {
    if (response.config && response.config._cancelSource) {
      pendingSources.delete(response.config._cancelSource)
    }
    return response
  },
  error => {
    if (error.config && error.config._cancelSource) {
      pendingSources.delete(error.config._cancelSource)
    }
    if (error.response && error.response.status === 401) {
      handleUnauthorized()
    }
    return Promise.reject(error)
  }
)

/* =========================
   VUE-RESOURCE
   ========================= */

Vue.http.interceptors.push((request, next) => {
  const token = getToken()

  if (token && isTokenExpired(token)) {
    // Trigger redirect; let the request fall through unauthenticated. The
    // response interceptor below would also catch the resulting 401, but
    // handleUnauthorized is idempotent so the duplicate is harmless.
    handleUnauthorized()
  } else if (token) {
    request.headers.set('Authorization', `Bearer ${token}`)
  }

  next(response => {
    if (response.status === 401) {
      handleUnauthorized()
    }
    return response
  })
})
